import React from "react";
import {
  HERO_BANNER,
  SHOP_CATEGORIES,
  TRAINING_FEATURED,
  STUDIO_GALLERY,
} from "../mock/mockData";
import { useCart } from "../components/LandingLayout";
import { useSiteContent } from "../hooks/useSiteContent";
import { NotifyBack } from "../components/NotifyBack";
import { ProductModal } from "../components/ProductModal";
import axios from "axios";
import { Link } from "react-router-dom";

const API_BASE = process.env.REACT_APP_BACKEND_URL
  ? `${process.env.REACT_APP_BACKEND_URL}/api`
  : "/api";

function PromoBar() {
  return (
    <div className="mb-6 rounded-full bg-black px-5 py-2 text-center text-[10px] font-medium uppercase tracking-[0.25em] text-white">
      Luxury Lash Training & Pro Supplies for Artists
    </div>
  );
}

function HeroSection({ heroImage }) {
  return (
    <section className="grid gap-8 md:grid-cols-[1.15fr_0.85fr] md:items-center">
      <div className="space-y-6">
        <p className="text-[11px] tracking-[0.25em] uppercase text-neutral-500">
          {HERO_BANNER.eyebrowText}
        </p>
        <h1 className="text-3xl font-medium tracking-tight text-neutral-900 sm:text-4xl md:text-[2.7rem]">
          {HERO_BANNER.title}
        </h1>
        <p className="max-w-lg text-sm leading-relaxed text-neutral-600">
          {HERO_BANNER.subtitle}
        </p>
        <div className="flex flex-wrap gap-3 text-[11px] font-medium uppercase tracking-[0.25em]">
          <Link
            to={HERO_BANNER.ctaPrimary.to}
            className="inline-flex h-10 items-center justify-center rounded-full bg-black px-8 text-white transition-colors duration-150 hover:bg-neutral-800"
          >
            {HERO_BANNER.ctaPrimary.label}
          </Link>
          <Link
            to={HERO_BANNER.ctaSecondary.to}
            className="inline-flex h-10 items-center justify-center rounded-full border border-neutral-800 px-8 text-neutral-900 transition-colors duration-150 hover:bg-neutral-900 hover:text-white"
          >
            {HERO_BANNER.ctaSecondary.label}
          </Link>
        </div>
      </div>
      <div className="overflow-hidden rounded-3xl border border-neutral-200 bg-white shadow-sm">
        <img
          src={heroImage}
          alt="Lash Dollz Vk Beauty hero"
          className="h-full w-full object-cover"
        />
      </div>
    </section>
  );
}

// Product card for native shop products used on the home "Essentials" row
function ProductCard({ product }) {
  const { addToCart } = useCart();
  const [open, setOpen] = React.useState(false);
  const priceNumber = Number(product.price || 0);

  return (
    <article className="group flex flex-col overflow-hidden rounded-3xl border border-neutral-200 bg-white" data-testid={`home-product-${product.id}`}>
      <div className="relative overflow-hidden">
        {product.image ? (
          <img
            src={product.image}
            alt={product.name}
            onClick={() => setOpen(true)}
            data-testid={`home-view-product-${product.id}`}
            className="h-64 w-full cursor-pointer object-cover transition-transform duration-300 group-hover:scale-105"
          />
        ) : (
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="flex h-64 w-full items-center justify-center bg-gradient-to-br from-[#f3ece8] to-[#e7dcd6]"
          >
            <span className="text-[11px] uppercase tracking-[0.3em] text-neutral-400">
              Lash Dollz
            </span>
          </button>
        )}
      </div>
      <div className="flex flex-1 flex-col px-4 py-4 text-sm">
        <h3 onClick={() => setOpen(true)} className="cursor-pointer text-neutral-900 hover:underline">
          {product.name}
        </h3>
        {product.description && (
          <p className="mt-1 line-clamp-2 text-xs text-neutral-500">
            {product.description}
          </p>
        )}
        {priceNumber > 0 && (
          <div className="mt-3 flex items-baseline gap-2 text-sm">
            <span className="font-medium text-neutral-900">
              €{priceNumber.toFixed(2)}
            </span>
            {product.stock === 0 && (
              <span
                data-testid={`home-sold-out-${product.id}`}
                className="text-[11px] font-medium uppercase tracking-[0.2em] text-red-500"
              >
                Sold out
              </span>
            )}
            {product.stock > 0 && product.stock <= 5 && (
              <span
                data-testid={`home-low-stock-${product.id}`}
                className="text-[11px] font-medium uppercase tracking-[0.2em] text-amber-600"
              >
                Only {product.stock} left
              </span>
            )}
          </div>
        )}
        <button
          type="button"
          data-testid={`home-add-to-cart-${product.id}`}
          disabled={product.stock === 0}
          onClick={() => {
            if (product.stock === 0) return;
            addToCart({
              id: product.id,
              name: product.name,
              price: priceNumber,
            });
          }}
          className="mt-4 inline-flex h-9 items-center justify-center rounded-full border border-neutral-900 px-6 text-[11px] font-medium uppercase tracking-[0.25em] text-neutral-900 transition-colors duration-150 hover:bg-neutral-900 hover:text-white disabled:cursor-not-allowed disabled:border-neutral-200 disabled:text-neutral-300 disabled:hover:bg-transparent disabled:hover:text-neutral-300"
        >
          {product.stock === 0 ? "Sold out" : "Add to cart"}
        </button>
        {product.stock === 0 && (
          <NotifyBack productId={product.id} testidPrefix="home-notify" />
        )}
      </div>
      {open && <ProductModal product={product} onClose={() => setOpen(false)} />}
    </article>
  );
}

function EssentialsSection() {
  const [products, setProducts] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    let cancelled = false;
    const fetchProducts = async () => {
      try {
        const res = await axios.get(`${API_BASE}/products`);
        if (!cancelled) setProducts((res.data || []).slice(0, 4));
      } catch (e) {
        if (!cancelled)
          setError("Products are temporarily unavailable. Please try again.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchProducts();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <section className="mt-10">
      <div className="flex items-baseline justify-between gap-4">
        <h2 className="text-sm font-medium uppercase tracking-[0.3em] text-neutral-700">
          Lash Dollz Essentials
        </h2>
        <Link
          to="/shop"
          className="text-[11px] uppercase tracking-[0.25em] text-neutral-500 underline-offset-4 hover:text-neutral-900 hover:underline"
        >
          View all
        </Link>
      </div>
      {loading && (
        <p className="mt-4 text-xs text-neutral-500">Loading products…</p>
      )}
      {error && !loading && (
        <p className="mt-4 text-xs text-neutral-500">{error}</p>
      )}
      {!loading && !error && (
        <div className="mt-5 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
          {products.map((product) => (
            <ProductCard key={product.id} product={product} />
          ))}
        </div>
      )}
    </section>
  );
}

function ShopByCategorySection() {
  return (
    <section className="mt-16">
      <h2 className="text-sm font-medium uppercase tracking-[0.3em] text-neutral-700">
        Shop by category
      </h2>
      <div className="mt-6 grid gap-5 md:grid-cols-3">
        {SHOP_CATEGORIES.map((cat) => (
          <Link
            key={cat.id}
            to={cat.to}
            className="group flex flex-col overflow-hidden rounded-3xl border border-neutral-200 bg-white"
          >
            {cat.imageUrl && (
              <div className="overflow-hidden">
                <img
                  src={cat.imageUrl}
                  alt={cat.title}
                  className="h-56 w-full object-cover transition-transform duration-300 group-hover:scale-105"
                />
              </div>
            )}
            <div className="px-4 py-4">
              <p className="text-xs font-medium uppercase tracking-[0.25em] text-neutral-800">
                {cat.title}
              </p>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}

function StudioResultsSection({ gallery }) {
  if (!gallery || gallery.length === 0) return null;

  const primary = gallery[0];
  const secondary = gallery.slice(1, 3);
  const tertiary = gallery[3];

  return (
    <section className="mt-16">
      <div className="flex flex-col justify-between gap-4 md:flex-row md:items-end">
        <div>
          <h2 className="text-sm font-medium uppercase tracking-[0.3em] text-neutral-700">
            Studio results
          </h2>
          <p className="mt-2 max-w-md text-sm text-neutral-600">
            Real work from Lash Dollz Vk Beauty—from fluffy lash sets to
            award-winning artistry. You&apos;ll be able to swap in more lash and lip
            blush photos here.
          </p>
        </div>
        <p className="text-[11px] uppercase tracking-[0.25em] text-neutral-500">
          Lash Extensions · Permanent Makeup · Lip Blush
        </p>
      </div>

      <div className="mt-6 grid gap-4 md:grid-cols-3">
        {primary && (
          <figure className="relative overflow-hidden rounded-3xl border border-neutral-200 bg-white">
            <img
              src={primary.imageUrl}
              alt={primary.label}
              className="h-80 w-full object-cover md:h-full"
            />
            <figcaption className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent px-4 pb-4 pt-10 text-xs text-white">
              <p className="font-medium">{primary.label}</p>
              <p className="text-[11px] uppercase tracking-[0.25em]">
                {primary.service}
              </p>
            </figcaption>
          </figure>
        )}

        <div className="grid gap-4">
          {secondary.map((item) => (
            <figure
              key={item.id}
              className="relative overflow-hidden rounded-3xl border border-neutral-200 bg-white"
            >
              <img
                src={item.imageUrl}
                alt={item.label}
                className="h-40 w-full object-cover"
              />
              <figcaption className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent px-4 pb-3 pt-6 text-[11px] text-white">
                <p className="font-medium leading-tight">{item.label}</p>
              </figcaption>
            </figure>
          ))}
        </div>

        {tertiary && (
          <figure className="relative overflow-hidden rounded-3xl border border-neutral-200 bg-white">
            <img
              src={tertiary.imageUrl}
              alt={tertiary.label}
              className="h-80 w-full object-cover md:h-full"
            />
            <figcaption className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent px-4 pb-4 pt-10 text-xs text-white">
              <p className="font-medium">{tertiary.label}</p>
              <p className="text-[11px] uppercase tracking-[0.25em]">
                {tertiary.service}
              </p>
            </figcaption>
          </figure>
        )}
      </div>
    </section>
  );
}

function GallerySection({ gallery }) {
  if (!gallery || gallery.length === 0) return null;

  return (
    <section className="mt-16">
      <div className="flex items-baseline justify-between gap-4">
        <h2 className="text-sm font-medium uppercase tracking-[0.3em] text-neutral-700">
          Gallery
        </h2>
        <p className="text-[11px] uppercase tracking-[0.25em] text-neutral-500">
          A closer look at Lash Dollz Vk Beauty work
        </p>
      </div>
      <div className="mt-5 grid gap-4 sm:grid-cols-2 md:grid-cols-3">
        {gallery.map((item) => (
          <figure
            key={item.id}
            className="overflow-hidden rounded-3xl border border-neutral-200 bg-white"
          >
            <img
              src={item.imageUrl}
              alt={item.label}
              className="h-52 w-full object-cover"
            />
            <figcaption className="px-4 py-3 text-xs text-neutral-600">
              <p className="font-medium text-neutral-900">{item.label}</p>
              <p className="mt-0.5 text-[11px] uppercase tracking-[0.23em] text-neutral-500">
                {item.service}
              </p>
            </figcaption>
          </figure>
        ))}
      </div>
    </section>
  );
}

export default function HomePage() {
  const content = useSiteContent();
  const heroImage = content?.hero_image || HERO_BANNER.imageUrl;
  const gallery =
    content?.gallery && content.gallery.length ? content.gallery : STUDIO_GALLERY;
  return (
    <div className="pb-12">
      <PromoBar />
      <HeroSection heroImage={heroImage} />
      <EssentialsSection />
      <ShopByCategorySection />
      <StudioResultsSection gallery={gallery} />
      <GallerySection gallery={gallery} />
    </div>
  );
}
